import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Stack, Typography, InputBase, Button, IconButton, Chip, Skeleton, Menu, MenuItem} from '@mui/material';
import {useAtom} from 'jotai';
import {HugeiconsIcon} from '@hugeicons/react';
import {Search01Icon, Upload01Icon, SparklesIcon, File02Icon, Delete01Icon, Clock01Icon, Layers01Icon, SdCardIcon, MoreVerticalIcon} from '@hugeicons/core-free-icons';
import {toast} from 'react-toastify';
import {KnowledgeManagerRepository, mapDocument} from '../../../../repositories/KnowledgeManagerRepository';
import {ConfirmModalNew} from '../../mcp-discovery/components/ConfirmModalNew';
import {useStompWebsocketKbManager} from '../../../../hooks/useStompWebsocketKbManager';
import {documentsAtom, documentsLoadingAtom} from '../atoms';
import {DocumentUploadModalNew} from './DocumentUploadModalNew';
import {DocumentPreviewModalNew} from './DocumentPreviewModalNew';
import {CaptureKnowledgeModalNew} from './CaptureKnowledgeModalNew';

const controlSx = {height: 36, display: "flex", alignItems: "center", gap: 1, px: 1.25, borderRadius: "10px", bgcolor: "#FAFAFD", color: "#6B6B80", fontSize: 13};

const SIZE_COL_W = 76;
const CHUNKS_COL_W = 90;
const UPDATED_COL_W = 120;
const ACTIONS_COL_W = 36;

const formatSize = (bytes) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
};

const statusStyle = (status) => {
  if (status === "indexed") return {color: '#2F9A6A', bg: 'rgba(47, 154, 106, 0.1)'};
  if (status === "processing" || status === "uploading") return {color: '#D9A13A', bg: 'rgba(217, 161, 58, 0.12)'};
  if (status === "failed") return {color: '#DC2626', bg: 'rgba(220, 38, 38, 0.1)'};
  return {color: '#8A8AA0', bg: '#F2F2F7'};
};

const TYPE_LABELS = {
  'application/pdf': 'PDF',
  'text/markdown': 'Markdown',
  'text/plain': 'Text',
  'text/csv': 'CSV',
  'application/json': 'JSON',
  'text/html': 'HTML',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
};

const docTypeLabel = (doc) => {
  if (doc.type && TYPE_LABELS[doc.type]) return TYPE_LABELS[doc.type];
  const ext = doc.name?.includes('.') ? doc.name.split('.').pop().toUpperCase() : null;
  return ext || 'File';
};

const hexToRgba = (hex, alpha) => {
  const clean = (hex || "#8491F7").replace("#", "");
  const full = clean.length === 3 ? clean.split("").map(c => c + c).join("") : clean;
  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ACTIVE_STATUSES = ["uploading", "processing"];

export const DocumentsTab = ({topic, onDocumentCountChanged}) => {
  const [documents, setDocuments] = useAtom(documentsAtom);
  const [loading, setLoading] = useAtom(documentsLoadingAtom);
  const [search, setSearch] = useState("");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [deleteDoc, setDeleteDoc] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionsMenu, setActionsMenu] = useState(null);
  const pollRef = useRef(null);

  const loadDocuments = useCallback(async (silent) => {
    if (!topic?.id) return;
    if (!silent) setLoading(true);
    try {
      const list = await KnowledgeManagerRepository.listDocuments(topic.id);
      setDocuments(list);
    } catch (err) {
      if (!silent) toast.error("Failed to load documents");
      console.error(err);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [topic?.id, setDocuments, setLoading]);

  useEffect(() => {
    loadDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic?.id]);

  const topicDocuments = documents.filter(d => d.topicId === topic.id);

  const hasActive = topicDocuments.some(d => ACTIVE_STATUSES.includes(d.status));

  // Live status pushes while anything in this topic is still uploading/processing (e.g. a long
  // entity-extraction pass) - updates that document in place with zero REST calls, instead of
  // re-fetching the whole list on every change.
  const [wsUpdate] = useStompWebsocketKbManager(hasActive && topic?.id ? `/topic/topics/${topic.id}/documents` : null);
  useEffect(() => {
    if (!wsUpdate?.document) return;
    const updated = mapDocument(wsUpdate.document);
    setDocuments(prev => prev.some(d => d.id === updated.id) ? prev.map(d => d.id === updated.id ? updated : d) : prev);
  }, [wsUpdate, setDocuments]);

  // Slow fallback poll in case a push is ever missed (dropped connection, etc.) - the WS push above
  // is the primary path, so this just needs to eventually catch up, not be fast.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (hasActive) {
      pollRef.current = setInterval(() => loadDocuments(true), 30000);
    }
    return () => clearInterval(pollRef.current);
  }, [hasActive, loadDocuments]);

  const filteredDocs = topicDocuments.filter(d => d.name?.toLowerCase().includes(search.toLowerCase()));

  const handleUploaded = async () => {
    setUploadOpen(false);
    await loadDocuments();
    await onDocumentCountChanged?.();
  };

  const handleCaptured = async () => {
    setCaptureOpen(false);
    await loadDocuments();
    await onDocumentCountChanged?.();
  };

  const handleDelete = async () => {
    if (!deleteDoc) return;
    setDeleting(true);
    try {
      await KnowledgeManagerRepository.deleteDocument(deleteDoc.id);
      setDocuments(list => list.filter(d => d.id !== deleteDoc.id));
      toast.success("Document deleted");
      setDeleteDoc(null);
      await onDocumentCountChanged?.();
    } catch (err) {
      toast.error("Failed to delete document");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Stack gap={2}>
      <Stack direction="row" gap={1.25} alignItems="center" justifyContent="space-between">
        <Box sx={{...controlSx, width: 220, flexShrink: 0}}>
          <HugeiconsIcon icon={Search01Icon} size={16}/>
          <InputBase
            placeholder="Search documents..." value={search} onChange={(e) => setSearch(e.target.value)}
            inputProps={{"aria-label": "Search documents"}} sx={{flex: 1, fontSize: 13, color: "#23264D"}}
          />
        </Box>
        <Stack direction="row" gap={1.25} sx={{flexShrink: 0}}>
          <Button
            startIcon={<HugeiconsIcon icon={Upload01Icon} size={16}/>}
            onClick={() => setUploadOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
              bgcolor: 'transparent', color: '#4B55C9', px: 2.5, whiteSpace: "nowrap",
              '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.12)'},
            }}
          >
            Upload
          </Button>
          <Button
            startIcon={<HugeiconsIcon icon={SparklesIcon} size={16}/>}
            onClick={() => setCaptureOpen(true)}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
              bgcolor: 'transparent', color: '#4B55C9', px: 2.5, whiteSpace: "nowrap",
              '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.12)'},
            }}
          >
            Quick Note
          </Button>
        </Stack>
      </Stack>

      {loading && topicDocuments.length === 0 ? (
        <Stack gap={1}>
          {Array.from({length: 4}).map((_, index) => (
            <Skeleton key={index} height={54} sx={{borderRadius: '12px'}}/>
          ))}
        </Stack>
      ) : filteredDocs.length === 0 ? (
        <Box sx={{py: 6, textAlign: "center"}}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: hexToRgba(topic.color, 0.12), color: topic.color || '#8491F7', mx: "auto", mb: 1.5,
          }}>
            <HugeiconsIcon icon={File02Icon} size={22}/>
          </Box>
          <Typography sx={{fontSize: 14, fontWeight: 700, color: '#23264D'}}>
            {topicDocuments.length === 0 ? "No documents yet" : "No documents match"}
          </Typography>
          <Typography sx={{fontSize: 12, color: '#8A8AA0', mt: 0.5}}>
            {topicDocuments.length === 0 ? "Upload documents to build this topic's knowledge base" : "Try a different search"}
          </Typography>
        </Box>
      ) : (
        <Stack gap={1}>
          <Stack direction="row" alignItems="center" gap={1.5} sx={{px: 1.5, display: {xs: "none", md: "flex"}}}>
            <Box sx={{width: 36, flexShrink: 0}}/>
            <Typography sx={{flex: 1, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0", textTransform: "uppercase"}}>
              Document
            </Typography>
            <Typography sx={{width: SIZE_COL_W, flexShrink: 0, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0", textTransform: "uppercase", textAlign: "right"}}>
              Size
            </Typography>
            <Typography sx={{width: CHUNKS_COL_W, flexShrink: 0, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0", textTransform: "uppercase", textAlign: "right"}}>
              Chunks
            </Typography>
            <Typography sx={{width: UPDATED_COL_W, flexShrink: 0, fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0", textTransform: "uppercase", textAlign: "right"}}>
              Updated
            </Typography>
            <Box sx={{width: ACTIONS_COL_W, flexShrink: 0}}/>
          </Stack>

          {filteredDocs.map(doc => {
            const style = statusStyle(doc.status);
            return (
              <Stack
                key={doc.id}
                direction="row" alignItems="center" gap={1.5}
                onClick={() => setPreviewDoc(doc)}
                sx={{p: 1.5, borderRadius: '12px', bgcolor: '#FAFAFD', cursor: "pointer", "&:hover": {bgcolor: '#F2F2F8'}}}
              >
                <Box sx={{
                  width: 36, height: 36, borderRadius: '10px', bgcolor: hexToRgba(topic.color, 0.12), display: "flex",
                  alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <HugeiconsIcon icon={File02Icon} size={16} color={topic.color || "#8491F7"}/>
                </Box>

                <Box sx={{flex: 1, minWidth: 0}}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Typography sx={{fontSize: 13, fontWeight: 600, color: '#23264D'}} noWrap>{doc.name}</Typography>
                    <Chip label={doc.status} size="small" sx={{height: 18, fontSize: 9, fontWeight: 700, color: style.color, bgcolor: style.bg}}/>
                  </Stack>
                  <Typography sx={{fontSize: 11, color: "#8A8AA0", mt: 0.25}}>{docTypeLabel(doc)}</Typography>

                  <Stack direction="row" gap={1.5} sx={{mt: 0.5, display: {xs: "flex", md: "none"}}}>
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <HugeiconsIcon icon={SdCardIcon} size={12} color={"#8A8AA0"}/>
                      <Typography sx={{fontSize: 11, color: "#8A8AA0"}}>{formatSize(doc.size)}</Typography>
                    </Stack>
                    {!!doc.chunks && (
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        <HugeiconsIcon icon={Layers01Icon} size={12} color={"#8A8AA0"}/>
                        <Typography sx={{fontSize: 11, color: "#8A8AA0"}}>{doc.chunks} chunks</Typography>
                      </Stack>
                    )}
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <HugeiconsIcon icon={Clock01Icon} size={12} color={"#8A8AA0"}/>
                      <Typography sx={{fontSize: 11, color: "#8A8AA0"}}>{formatDate(doc.uploadedAt)}</Typography>
                    </Stack>
                  </Stack>
                </Box>

                <Typography sx={{width: SIZE_COL_W, flexShrink: 0, fontSize: 12, color: "#6B6B80", textAlign: "right", display: {xs: "none", md: "block"}}}>
                  {formatSize(doc.size)}
                </Typography>
                <Typography sx={{width: CHUNKS_COL_W, flexShrink: 0, fontSize: 12, color: "#6B6B80", textAlign: "right", display: {xs: "none", md: "block"}}}>
                  {doc.chunks ? `${doc.chunks} chunks` : "—"}
                </Typography>
                <Typography sx={{width: UPDATED_COL_W, flexShrink: 0, fontSize: 12, color: "#6B6B80", textAlign: "right", display: {xs: "none", md: "block"}}} noWrap>
                  {formatDate(doc.uploadedAt)}
                </Typography>

                <Box sx={{width: ACTIONS_COL_W, flexShrink: 0, textAlign: "right"}} onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    size="small" aria-label={`Actions for ${doc.name}`}
                    onClick={(e) => setActionsMenu({anchor: e.currentTarget, doc})}
                    sx={{color: '#8A8AA0', '&:hover': {bgcolor: 'rgba(132, 145, 247, 0.08)'}}}
                  >
                    <HugeiconsIcon icon={MoreVerticalIcon} size={16}/>
                  </IconButton>
                </Box>
              </Stack>
            );
          })}
        </Stack>
      )}

      <Menu
        anchorEl={actionsMenu?.anchor}
        open={Boolean(actionsMenu)}
        onClose={() => setActionsMenu(null)}
        PaperProps={{sx: {borderRadius: '12px'}}}
      >
        <MenuItem
          onClick={() => {setPreviewDoc(actionsMenu.doc); setActionsMenu(null);}}
          sx={{gap: 1}}
        >
          <HugeiconsIcon icon={File02Icon} size={16}/><Typography sx={{fontSize: '13px'}}>View</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {setDeleteDoc(actionsMenu.doc); setActionsMenu(null);}}
          sx={{color: '#DC2626', gap: 1}}
        >
          <HugeiconsIcon icon={Delete01Icon} size={16}/><Typography sx={{fontSize: '13px'}}>Delete</Typography>
        </MenuItem>
      </Menu>

      <DocumentUploadModalNew topicId={topic.id} open={uploadOpen} onClose={() => setUploadOpen(false)} onUploaded={handleUploaded}/>

      <CaptureKnowledgeModalNew topicId={topic.id} open={captureOpen} onClose={() => setCaptureOpen(false)} onCaptured={handleCaptured}/>

      <DocumentPreviewModalNew
        document={previewDoc} open={!!previewDoc} onClose={() => setPreviewDoc(null)}
        onUpdated={async (updated) => {
          setPreviewDoc(updated);
          await loadDocuments(true);
        }}
      />

      <ConfirmModalNew
        open={!!deleteDoc}
        onClose={() => setDeleteDoc(null)}
        onConfirm={handleDelete}
        title={`Delete "${deleteDoc?.name}"?`}
        description="This will remove the document from the knowledge base. This action cannot be undone."
        confirmText="Delete Document"
        loading={deleting}
      />
    </Stack>
  );
};
